
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { useEffect, useState } from "react";
import { FaDownload } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { convertArrayOfObjectsToCSV, showToast } from "../../lib/utility";
import { middleware } from "../../redux/store";
import { uploadFileToFTP } from "../Report/ReportSlice";
import Modal from "./Modal";

applyPlugin(jsPDF);

export default function FileDownloadConfirm({
  fileType,
  data,
  reportName,
  chartsRef,
  mappable,
}) {
  const { t } = useTranslation("common");
  const [showModal, setShowModal] = useState(true);
  const [preparedFile, setPreparedFile] = useState(null);

  const dispatch = useDispatch();

  const createPDF = async (data) => {
  const isWalletReport = reportName.toLowerCase().includes("wallet");
  const isLatencyReport = reportName.toLowerCase().includes("latency");
  const isAdvanceReport = reportName.toLowerCase().includes("advance");
  const isLookupReport =
    reportName.toLowerCase().includes("lookup") ||
    reportName.toLowerCase().includes("look-up");
  
  let doc = isLookupReport
    ? new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" })
    : new jsPDF(
        isWalletReport || isLatencyReport || isAdvanceReport
          ? "landscape"
          : data.length < 7
          ? undefined
          : "landscape"
      );

if (chartsRef?.current?.[0]) {
  const canvas1 = await html2canvas(chartsRef.current[0], {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
  });
  const img1 = canvas1.toDataURL("image/png");
  if (isLatencyReport) {
    doc.addImage(img1, "PNG", 10, 18, 130, 75);
  } else if (isAdvanceReport) {
    doc.addImage(img1, "PNG", 12, 21, 90, 65);
  } else {
    doc.addImage(img1, "PNG", 12, 21, 90, 65);
  }
}

if (chartsRef?.current?.[1]) {
  const canvas2 = await html2canvas(chartsRef.current[1], {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
  });
  const img2 = canvas2.toDataURL("image/png");
  if (isLatencyReport) {
    doc.addImage(img2, "PNG", 148, 18, 130, 75);
  } else if (isAdvanceReport) {
    doc.addImage(img2, "PNG", 108, 21, 90, 65);
  } else {
    doc.addImage(img2, "PNG", 108, 21, 90, 65);
  }
}
    const yOffset = chartsRef ? (isLatencyReport ? 108 : 103) : 27;

    doc.setFontSize(16);
    doc.text(
      reportName.replaceAll(" ", "-"),
      13,
      chartsRef ? (isLatencyReport ? 100 : 97) : 20
    );
    doc.setFontSize(12);

    const headers = Object.keys(mappable([{}])[0]);
    let tableData = data.map((item) => headers.map((key) => item[key]));
    let formattedHeaders = headers;

    if (isWalletReport) {
      formattedHeaders = headers.map((header) => {
        return header.replace(/([A-Z])/g, " $1").trim();
      });

      tableData = data.map((item) =>
        headers.map((key) => {
          let value = item[key];

          if (key === "Time" && value && value !== "-") {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                value = date.toLocaleString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                });
              }
            } catch (e) {
            }
          }

          if (key === "Remarks" && value && value !== "-") {
            if (value.length > 30) {
              value = value.substring(0, 27) + "...";
            }
          }

          return String(value || "-");
        })
      );
    }

    const tableConfig = {
      startY: yOffset,
      head: [formattedHeaders],
      body: tableData,
    };

    if (isLookupReport) {
      formattedHeaders = headers.map((h) => {
        const map = {
          BatchId: "Batch\nId",
          LookupId: "Lookup\nId",
          SubmitOn: "Submit\nOn",
          Destination: "Destination",
          Status: "Status",
          ErrorCode: "Error\nCode",
          Error: "Error",
          Country: "Country",
          Operator: "Operator",
          MNC: "MNC",
          DeliveredOn: "Delivered\nOn",
          IsPorted: "Is\nPorted",
          IsRoaming: "Is\nRoaming",
          PortedOperater: "Ported\nOperator",
          RoamingCountry: "Roaming\nCountry",
          RoamingOperator: "Roaming\nOperator",
          PortedNNC: "Ported\nNNC",
          RoamingNNC: "Roaming\nNNC",
          RoamingCC: "Roaming\nCC",
          IMSI: "IMSI",
          MSC: "MSC",
        };
        return map[h] ?? h.replace(/([A-Z])/g, " $1").trim();
      });

      tableConfig.head = [formattedHeaders];

      tableConfig.styles = {
        fontSize: 5.5,
        cellPadding: 0.8,
        overflow: "ellipsize",
        valign: "middle",
      };
      tableConfig.headStyles = {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 6.5,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        cellPadding: 0.8,
        overflow: "linebreak",
      };

      tableConfig.horizontalPageBreak = false;

      const lookupBaseWidthByKey = {
        BatchId: 30,
        LookupId: 18, 
        SubmitOn: 24,
        Destination: 22,
        Status: 14,
        ErrorCode: 12,
        Error: 30, 
        Country: 14,
        Operator: 34, 
        MNC: 12,
        DeliveredOn: 22,
        IsPorted: 12,
        IsRoaming: 12,
        PortedOperater: 30, 
        RoamingCountry: 18,
        RoamingOperator: 22,
        PortedNNC: 14,
        RoamingNNC: 14,
        RoamingCC: 14,
        IMSI: 24,
        MSC: 18,
      };

      const lookupAlignCenterKeys = new Set([
        "LookupId",
        "Status",
        "ErrorCode",
        "MNC",
        "IsPorted",
        "IsRoaming",
        "PortedNNC",
        "RoamingNNC",
        "RoamingCC",
      ]);

      tableConfig.margin = { top: yOffset, left: 6, right: 6, bottom: 10 };
      const pageWidth = doc.internal.pageSize.getWidth();
      const availableWidth =
        pageWidth - tableConfig.margin.left - tableConfig.margin.right;

      let widths = headers.map((k) => lookupBaseWidthByKey[k] ?? 18);
      const sum = widths.reduce((a, b) => a + b, 0);
      const scale = sum > 0 ? Math.min(1, availableWidth / sum) : 1;
      widths = widths.map((w) => Math.max(7.5, w * scale));

      tableConfig.columnStyles = widths.reduce((acc, w, idx) => {
        const key = headers[idx];
        acc[idx] = {
          cellWidth: w,
          ...(lookupAlignCenterKeys.has(key) ? { halign: "center" } : {}),
          ...(key === "LookupId"
            ? { overflow: "hidden", halign: "center" }
            : {}),
          ...((key === "Error" || key === "Operator" || key === "PortedOperater")
            ? { overflow: "linebreak" }
            : {}),
        };
        return acc;
      }, {});

      tableConfig.tableWidth = availableWidth;
      tableConfig.showHead = "everyPage";
      tableConfig.pageBreak = "auto";
      tableConfig.rowPageBreak = "avoid";
    }

    if (isWalletReport) {
      tableConfig.styles = {
        fontSize: 7,
        cellPadding: 1.5,
        overflow: "linebreak",
        halign: "left",
        valign: "middle",
      };
      tableConfig.headStyles = {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 8,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      };
      tableConfig.columnStyles = {
        0: { cellWidth: 10 }, 
        1: { cellWidth: 18 }, 
        2: { cellWidth: 28 }, 
        3: { cellWidth: 12 }, 
        4: { cellWidth: 30 }, 
        5: { cellWidth: 16 }, 
        6: { cellWidth: 30 }, 
        7: { cellWidth: 22 }, 
        8: { cellWidth: 14 }, 
        9: { cellWidth: 14 }, 
        10: { cellWidth: 18 }, 
        11: { cellWidth: 18 }, 
        12: { cellWidth: 18 }, 
      };
      tableConfig.margin = { top: yOffset, left: 10, right: 10 };
      tableConfig.tableWidth = "auto";
      tableConfig.showHead = "everyPage";
      tableConfig.pageBreak = "auto";
      tableConfig.rowPageBreak = "avoid";
    }

    if (isLatencyReport) {
      tableConfig.styles = {
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      };
      tableConfig.headStyles = {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      };
      tableConfig.columnStyles = {
        0: { cellWidth: 28 }, 
        1: { cellWidth: 24 }, 
        2: { cellWidth: 24 }, 
        3: { cellWidth: 28 }, 
        4: { cellWidth: 20 }, 
        5: { cellWidth: 22 }, 
        6: { cellWidth: 20 }, 
        7: { cellWidth: 20 }, 
        8: { cellWidth: 20 }, 
        9: { cellWidth: 20 }, 
        10: { cellWidth: 20 }, 
        11: { cellWidth: 20 }, 
      };
      tableConfig.margin = { top: 12, left: 8, right: 8, bottom: 12 };
      tableConfig.tableWidth = "auto";
      tableConfig.showHead = "everyPage";
      tableConfig.pageBreak = "auto";
      tableConfig.rowPageBreak = "avoid";
    }


     if (isAdvanceReport) {
      // Header labels with explicit line breaks so long titles wrap at word
      // boundaries instead of breaking mid-word inside narrow columns.
      const advanceHeaderByKey = {
        "Message ID": "Message\nID",
        "Destination No": "Destination\nNo",
        "Original Source": "Original\nSource",
        "Error Code": "Error\nCode",
        "Response ID": "Response\nID",
        "Network ID": "Network\nID",
        "Original Network": "Original\nNetwork",
        "Ported Network ID": "Ported Network\nID",
        "Ported Operator": "Ported\nOperator",
        "Template ID": "Template\nID",
        "Deliver Time": "Deliver\nTime",
      };

      // Base column widths (mm) tuned for the advance report data. Columns are
      // scaled down proportionally if they exceed the printable page width so
      // the whole table fits one page width.
      const advanceBaseWidthByKey = {
        "Message ID": 20,
        "Username": 13,
        "Destination No": 11,
        "Original Source": 9,
        "Source": 8,
        "Status": 9,
        "Error Code": 8,
        "Description": 11,
        "Response ID": 9,
        "Smsc": 8,
        "Network ID": 9,
        "Country": 9,
        "Original Network": 10,
        "Ported Network ID": 10,
        "Ported Operator": 11,
        "P ID": 7,
        "Template ID": 9,
        "Tm ID": 12,
        "Dcs": 7,
        "Content": 30,
        "Submitted Time": 18,
        "Deliver Time": 18,
      };

      formattedHeaders = headers.map((h) => advanceHeaderByKey[h] ?? h);
      tableConfig.head = [formattedHeaders];

      const advanceMargin = { top: 12, left: 6, right: 6, bottom: 10 };
      const advancePageWidth = doc.internal.pageSize.getWidth();
      const advanceAvailableWidth =
        advancePageWidth - advanceMargin.left - advanceMargin.right;

      let advanceWidths = headers.map((k) => advanceBaseWidthByKey[k] ?? 10);
      const advanceSum = advanceWidths.reduce((a, b) => a + b, 0);
      const advanceScale =
        advanceSum > 0 ? advanceAvailableWidth / advanceSum : 1;
      advanceWidths = advanceWidths.map((w) => Math.max(5, w * advanceScale));

      tableConfig.styles = {
        fontSize: 5,
        cellPadding: 1,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      };
      tableConfig.headStyles = {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 5,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        overflow: "linebreak",
      };
      tableConfig.columnStyles = advanceWidths.reduce((acc, w, idx) => {
        acc[idx] = { cellWidth: w };
        return acc;
      }, {});
      tableConfig.margin = advanceMargin;
      tableConfig.tableWidth = advanceAvailableWidth;
      tableConfig.showHead = "everyPage";
      tableConfig.pageBreak = "auto";
      tableConfig.rowPageBreak = "avoid";
    }
    doc.autoTable(tableConfig);

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(9);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const x =
        isWalletReport || isLatencyReport ? 140 : data.length < 7 ? 98 : 140;
      const y = doc.internal.pageSize.getHeight() - 10;
      doc.text(`Pages ${i} - ${pageCount}`, x, y);
    }

    return doc;
  };

  const prepareFile = async () => {
    if (!data.length) return;

    const fileName = `${reportName.replaceAll(" ", "-")}.${fileType.value}`;
    let blob = null;

    if (fileType.value === "xlsx") {
      const ws = XLSX.utils.json_to_sheet(data);

      // Auto-fit column widths to the header and the longest cell value so
      // long content (Message ID, Content, timestamps) is not visually cut
      // off when the file is opened.
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((header) => {
        let maxLen = header.length;
        for (const row of data) {
          const len = String(row[header] ?? "").length;
          if (len > maxLen) maxLen = len;
        }
        return { wch: Math.min(maxLen + 2, 60) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const xlsBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      blob = new Blob([xlsBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
    } else if (fileType.value === "csv") {
      const csv = convertArrayOfObjectsToCSV(data);
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    } else if (fileType.value === "pdf") {
      const doc = await createPDF(data);
      const pdfBlob = doc.output("blob");
      blob = pdfBlob;
    }

    if (blob) {
      setPreparedFile({ blob, name: fileName, type: fileType.value });
    }
  };

  const toggleModal = () => setShowModal((prev) => !prev);

  const saveToLocal = () => {
    if (!preparedFile) return;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(preparedFile.blob);
    link.download = preparedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toggleModal();
  };

  const sendToFTP = async () => {
    if (!preparedFile) return;

    dispatch(uploadFileToFTP({ preparedFile, reportName })).then((action) =>
      middleware(
        action,
        "File Upload to FTP",
        () => {
          showToast(
            t("fileUploadedSuccessfully", {
              defaultValue: "File Uploaded Successfully",
            }),
            "success"
          );
          toggleModal();
        },
        undefined,
        { modal: toggleModal }
      )
    );
  };

  useEffect(() => {
    if (!preparedFile) prepareFile();
    if (!showModal) {
      const modalElement = document.getElementById(
        "file-download-confirm-container"
      );
      if (modalElement) document.body.removeChild(modalElement);
    }
  }, [showModal]);

  return (
    <>
      <Modal
        show={showModal && preparedFile}
        close={() => setShowModal(false)}
        justify="center"
      >
        <div className="modal-header">
          <h6 className="modal-title fs-22">
            <FaDownload className="align-text-bottom me-2 text-blue" />
            {t("downloadModal.title", { defaultValue: "File Downloaded" })}
          </h6>
          <svg
            className="cursor-pointer"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={toggleModal}
          >
            <path
              d="M20 4L4 20M4 4L20 20"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="modal-body py-2 px-3 bg-white">
          <p className="fs-16 fw-600">
            {t("downloadModal.message", {
              defaultValue: "Please select an option to continue.",
            })}
          </p>
        </div>
        <div className="modal-footer d-block bg-white">
          <div className="row g-3">
            <div className="col-6 mt-0">
              <button
                className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                onClick={saveToLocal}
              >
                {t("downloadModal.saveToLocal", {
                  defaultValue: "Save to Local",
                })}
              </button>
            </div>
            <div className="col-6 mt-0">
              <button
                className="cbtn text-white btn w-100 fs-16 fw-500 br-12 px-4 py-2 border_lightgray_1"
                onClick={sendToFTP}
              >
                {t("downloadModal.sendToFtp", {
                  defaultValue: "Send to FTP Server",
                })}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
